import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  DocumentIcon,
  BookOpenIcon,
  TrashIcon,
  Squares2X2Icon,
  ArchiveBoxIcon,
} from "@heroicons/react/24/outline";
import {
  useGetUnlinkedLessons,
  useGetUnlinkedPages,
  useGetDeletedPages,
  useGetTopics,
  useGetArchivedPages,
} from "../../api/queries";
import {
  useDeleteLesson,
  useDeletePage,
  useRestorePage,
  useDeleteModule,
  useArchivePage,
  useUnarchivePage,
} from "../../api/mutations";
import Button from "../../componentLibrary/Button";
import Container from "../../componentLibrary/Container";
import Spinner from "../../componentLibrary/Spinner";
import ConfirmationBox from "../../componentLibrary/ConfirmationBox";
import { useNotification } from "../../hooks/useNotification";
import { LessonI, PageI, ModuleI } from "../../types";
import LessonPreview from "./components/LessonPreview";
import PagePreview from "./components/PagePreview";
import ModulePreview from "./components/ModulePreview";
import LessonsTabContent from "./components/LessonsTabContent";
import PagesTabContent from "./components/PagesTabContent";
import DeletedPagesTabContent from "./components/DeletedPagesTabContent";
import ModulesTabContent from "./components/ModulesTabContent";
import ArchivedPagesTabContent from "./components/ArchivedPagesTabContent";

type SelectedItem = {
  type: "lesson" | "page" | "deletedPage" | "module" | "archivedPage";
  data: LessonI | PageI | ModuleI;
} | null;

const ContentDeletePage = () => {
  const queryClient = useQueryClient();
  const { notify } = useNotification();

  const [selectedItem, setSelectedItem] = useState<SelectedItem>(null);
  const [activeTab, setActiveTab] = useState<
    "modules" | "lessons" | "pages" | "pendingDeletion" | "archived"
  >("modules");

  const {
    data: allModules = [],
    isLoading: modulesLoading,
    isError: modulesError,
  } = useGetTopics();

  const {
    data: unlinkedLessons = [],
    isLoading: lessonsLoading,
    isError: lessonsError,
  } = useGetUnlinkedLessons();

  const {
    data: unlinkedPages = [],
    isLoading: pagesLoading,
    isError: pagesError,
  } = useGetUnlinkedPages();

  const {
    data: deletedPages = [],
    isLoading: deletedPagesLoading,
    isError: deletedPagesError,
  } = useGetDeletedPages();

  const {
    data: archivedPages = [],
    isLoading: archivedPagesLoading,
    isError: archivedPagesError,
  } = useGetArchivedPages();

  // Delete confirmation state
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    type: "module" | "lesson" | "page";
    id: string;
    title: string;
  } | null>(null);

  const deleteModuleMutation = useDeleteModule({
    onSuccess: async () => {
      notify("success", "Module deleted successfully");
      await queryClient.invalidateQueries({ queryKey: ["modules"] });
      // Invalidate unlinked lessons query since lessons from deleted module may now be unlinked
      await queryClient.invalidateQueries({
        queryKey: ["lessons", "unlinked"],
      });
      setItemToDelete(null);
      // Clear selection if deleted item was selected
      if (
        selectedItem?.type === "module" &&
        selectedItem.data._id === itemToDelete?.id
      ) {
        setSelectedItem(null);
      }
    },
    onError: async () => {
      notify("error", "There was an error deleting the module.");
      setItemToDelete(null);
    },
  });

  const deleteLessonMutation = useDeleteLesson({
    onSuccess: async () => {
      notify("success", "Lesson deleted successfully");
      await queryClient.invalidateQueries({
        queryKey: ["lessons", "unlinked"],
      });
      // Invalidate unlinked pages query since pages from deleted lesson may now be unlinked
      await queryClient.invalidateQueries({ queryKey: ["pages", "unlinked"] });
      setItemToDelete(null);
      // Clear selection if deleted item was selected
      if (
        selectedItem?.type === "lesson" &&
        selectedItem.data._id === itemToDelete?.id
      ) {
        setSelectedItem(null);
      }
    },
    onError: async () => {
      notify("error", "There was an error deleting the lesson.");
      setItemToDelete(null);
    },
  });

  const deletePageMutation = useDeletePage({
    onSuccess: async () => {
      notify(
        "success",
        "Page marked for deletion. It will be permanently deleted in 7 days.",
      );
      await queryClient.invalidateQueries({ queryKey: ["pages"] });
      setItemToDelete(null);
      // Clear selection if deleted item was selected
      if (
        (selectedItem?.type === "page" ||
          selectedItem?.type === "archivedPage") &&
        selectedItem.data._id === itemToDelete?.id
      ) {
        setSelectedItem(null);
      }
    },
    onError: async () => {
      notify("error", "There was an error deleting the page.");
      setItemToDelete(null);
    },
  });

  const restorePageMutation = useRestorePage({
    onSuccess: async () => {
      notify("success", "Page restored successfully");
      await queryClient.invalidateQueries({ queryKey: ["pages"] });
      setSelectedItem(null);
    },
    onError: async () => {
      notify("error", "There was an error restoring the page.");
    },
  });

  const archivePageMutation = useArchivePage({
    onSuccess: async () => {
      notify("success", "Page archived successfully");
      await queryClient.invalidateQueries({ queryKey: ["pages"] });
      setSelectedItem(null);
    },
    onError: async () => {
      notify("error", "There was an error archiving the page.");
    },
  });

  const unarchivePageMutation = useUnarchivePage({
    onSuccess: async () => {
      notify("success", "Page unarchived successfully");
      await queryClient.invalidateQueries({ queryKey: ["pages"] });
      setSelectedItem(null);
    },
    onError: async () => {
      notify("error", "There was an error unarchiving the page.");
    },
  });

  const handleRestorePage = (pageId: string, title: string) => {
    notify("info", `Restoring "${title}"...`);
    restorePageMutation.mutate(pageId);
  };

  const handleArchivePage = (pageId: string) => {
    archivePageMutation.mutate(pageId);
  };

  const handleUnarchivePage = (pageId: string) => {
    unarchivePageMutation.mutate(pageId);
  };

  const handleDeleteClick = (
    type: "module" | "lesson" | "page",
    id: string,
    title: string,
  ) => {
    setItemToDelete({ type, id, title });
    setIsDeleteConfirmationOpen(true);
  };

  const handleConfirmDelete = () => {
    if (itemToDelete) {
      if (itemToDelete.type === "module") {
        deleteModuleMutation.mutate(itemToDelete.id);
      } else if (itemToDelete.type === "lesson") {
        deleteLessonMutation.mutate(itemToDelete.id);
      } else {
        deletePageMutation.mutate(itemToDelete.id);
      }
    }
    setIsDeleteConfirmationOpen(false);
  };

  const isLoading =
    modulesLoading ||
    lessonsLoading ||
    pagesLoading ||
    deletedPagesLoading ||
    archivedPagesLoading;
  const isError =
    modulesError ||
    lessonsError ||
    pagesError ||
    deletedPagesError ||
    archivedPagesError;

  if (isLoading) return <Spinner className="mx-auto mt-8" />;
  if (isError)
    return <div className="text-center mt-8">Error loading content</div>;

  const hasUnlinkedContent =
    allModules.length > 0 ||
    unlinkedLessons.length > 0 ||
    unlinkedPages.length > 0;
  const hasDeletedContent = deletedPages.length > 0;
  const hasArchivedContent = archivedPages.length > 0;
  const hasAnyContent =
    hasUnlinkedContent || hasDeletedContent || hasArchivedContent;

  const renderContentPreview = () => {
    if (!selectedItem) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-400">
          <DocumentIcon className="w-16 h-16 mb-4" />
          <p className="text-lg">Select an item to preview its contents</p>
        </div>
      );
    }

    if (selectedItem.type === "module") {
      return (
        <ModulePreview
          module={selectedItem.data as ModuleI}
          onDeleteClick={handleDeleteClick}
        />
      );
    }

    if (selectedItem.type === "lesson") {
      return (
        <LessonPreview
          lesson={selectedItem.data as LessonI}
          onDeleteClick={handleDeleteClick}
        />
      );
    }

    if (selectedItem.type === "page") {
      return (
        <PagePreview
          page={selectedItem.data as PageI}
          onDeleteClick={handleDeleteClick}
          onArchiveClick={handleArchivePage}
          isArchiving={archivePageMutation.isPending}
        />
      );
    }

    if (selectedItem.type === "deletedPage") {
      const page = selectedItem.data as PageI;
      return (
        <div className="h-full overflow-y-auto">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {page.title || "Untitled Page"}
              </h3>
              <p className="text-sm text-amber-600 mt-1">
                This page is pending deletion
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleRestorePage(page._id!, page.title)}
              disabled={restorePageMutation.isPending}
              loading={restorePageMutation.isPending}
            >
              Restore Page
            </Button>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-amber-800">
              <strong>Deleted:</strong>{" "}
              {page.deletionRequestedAt
                ? new Date(page.deletionRequestedAt).toLocaleString()
                : "Unknown"}
            </p>
            <p className="text-sm text-amber-800 mt-1">
              <strong>Permanent deletion:</strong>{" "}
              {page.deletionRequestedAt
                ? new Date(
                    new Date(page.deletionRequestedAt).getTime() +
                      7 * 24 * 60 * 60 * 1000,
                  ).toLocaleString()
                : "Unknown"}
            </p>
          </div>
          <div className="text-sm text-gray-600">
            <p>
              <strong>Components:</strong> {page.components?.length || 0}
            </p>
            {page.keyTakeaways && (
              <p className="mt-2">
                <strong>Key Takeaways:</strong> {page.keyTakeaways}
              </p>
            )}
          </div>
        </div>
      );
    }

    if (selectedItem.type === "archivedPage") {
      const page = selectedItem.data as PageI;
      return (
        <div className="h-full overflow-y-auto">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {page.title || "Untitled Page"}
              </h3>
              <p className="text-sm text-purple-600 mt-1">
                This page is archived
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleUnarchivePage(page._id!)}
                disabled={unarchivePageMutation.isPending}
                loading={unarchivePageMutation.isPending}
              >
                Unarchive Page
              </Button>
              <Button
                variant="cautionary"
                size="sm"
                onClick={() =>
                  handleDeleteClick(
                    "page",
                    page._id!,
                    page.title || "Untitled Page",
                  )
                }
                disabled={deletePageMutation.isPending}
                loading={deletePageMutation.isPending}
                className="flex items-center gap-1"
              >
                <TrashIcon className="w-4 h-4" />
                Delete
              </Button>
            </div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-purple-800">
              <strong>Archived:</strong>{" "}
              {page.archivedAt
                ? new Date(page.archivedAt).toLocaleString()
                : "Unknown"}
            </p>
            <p className="text-sm text-purple-800 mt-2">
              This page has been archived and is hidden from the content
              manager. It can be restored at any time by unarchiving it.
            </p>
          </div>
          <div className="text-sm text-gray-600">
            <p>
              <strong>Components:</strong> {page.components?.length || 0}
            </p>
            {page.keyTakeaways && (
              <p className="mt-2">
                <strong>Key Takeaways:</strong> {page.keyTakeaways}
              </p>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="p-2 sm:p-4 h-[calc(100vh-8rem)]">
      <Container className="h-full">
        <div className="flex flex-col h-full">
          <div className="mb-4">
            <h2 className="text-2xl font-bold">Content Delete</h2>
            <p className="text-gray-600 text-sm">
              View and delete empty modules, unlinked lessons, and unlinked
              pages.
            </p>
          </div>

          {!hasAnyContent ? (
            <div className="flex-1 flex items-center justify-center bg-gray-50 rounded-lg">
              <div className="text-center">
                <DocumentIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg">
                  No empty, unlinked, or pending deletion content found.
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  All modules, lessons, and pages are properly linked.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-0">
              {/* Left Sidebar - Unlinked Content List */}
              <div className="lg:col-span-4 flex flex-col min-h-0 bg-white rounded-lg border overflow-hidden">
                {/* Tabs */}
                <div className="flex border-b">
                  <button
                    className={`flex-1 px-1 py-2 text-xs font-medium flex flex-col items-center justify-center gap-0.5 ${
                      activeTab === "modules"
                        ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                    onClick={() => {
                      setActiveTab("modules");
                      setSelectedItem(null);
                    }}
                  >
                    <Squares2X2Icon className="w-4 h-4" />
                    <span>Modules</span>
                    <span>({allModules.length})</span>
                  </button>
                  <button
                    className={`flex-1 px-1 py-2 text-xs font-medium flex flex-col items-center justify-center gap-0.5 ${
                      activeTab === "lessons"
                        ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                    onClick={() => {
                      setActiveTab("lessons");
                      setSelectedItem(null);
                    }}
                  >
                    <BookOpenIcon className="w-4 h-4" />
                    <span>Lessons</span>
                    <span>({unlinkedLessons.length})</span>
                  </button>
                  <button
                    className={`flex-1 px-1 py-2 text-xs font-medium flex flex-col items-center justify-center gap-0.5 ${
                      activeTab === "pages"
                        ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                    onClick={() => {
                      setActiveTab("pages");
                      setSelectedItem(null);
                    }}
                  >
                    <DocumentIcon className="w-4 h-4" />
                    <span>Pages</span>
                    <span>({unlinkedPages.length})</span>
                  </button>
                  <button
                    className={`flex-1 px-1 py-2 text-xs font-medium flex flex-col items-center justify-center gap-0.5 ${
                      activeTab === "pendingDeletion"
                        ? "text-amber-600 border-b-2 border-amber-600 bg-amber-50"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                    onClick={() => {
                      setActiveTab("pendingDeletion");
                      setSelectedItem(null);
                    }}
                  >
                    <TrashIcon className="w-4 h-4" />
                    <span>Pending</span>
                    <span>({deletedPages.length})</span>
                  </button>
                  <button
                    className={`flex-1 px-1 py-2 text-xs font-medium flex flex-col items-center justify-center gap-0.5 ${
                      activeTab === "archived"
                        ? "text-purple-600 border-b-2 border-purple-600 bg-purple-50"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                    onClick={() => {
                      setActiveTab("archived");
                      setSelectedItem(null);
                    }}
                  >
                    <ArchiveBoxIcon className="w-4 h-4" />
                    <span>Archived</span>
                    <span>({archivedPages.length})</span>
                  </button>
                </div>

                {/* Content List */}
                <div className="flex-1 overflow-y-auto">
                  {activeTab === "modules" && (
                    <ModulesTabContent
                      modules={allModules}
                      selectedModuleId={
                        selectedItem?.type === "module"
                          ? selectedItem.data._id
                          : null
                      }
                      onSelectModule={(module) =>
                        setSelectedItem({ type: "module", data: module })
                      }
                    />
                  )}

                  {activeTab === "lessons" && (
                    <LessonsTabContent
                      lessons={unlinkedLessons}
                      selectedLessonId={
                        selectedItem?.type === "lesson"
                          ? selectedItem.data._id
                          : null
                      }
                      onSelectLesson={(lesson) =>
                        setSelectedItem({ type: "lesson", data: lesson })
                      }
                    />
                  )}

                  {activeTab === "pages" && (
                    <PagesTabContent
                      pages={unlinkedPages}
                      selectedPageId={
                        selectedItem?.type === "page"
                          ? selectedItem.data._id
                          : null
                      }
                      onSelectPage={(page) =>
                        setSelectedItem({ type: "page", data: page })
                      }
                    />
                  )}

                  {activeTab === "pendingDeletion" && (
                    <DeletedPagesTabContent
                      pages={deletedPages}
                      selectedPageId={
                        selectedItem?.type === "deletedPage"
                          ? selectedItem.data._id
                          : null
                      }
                      onSelectPage={(page) =>
                        setSelectedItem({ type: "deletedPage", data: page })
                      }
                      onRestorePage={handleRestorePage}
                      isRestoring={restorePageMutation.isPending}
                    />
                  )}

                  {activeTab === "archived" && (
                    <ArchivedPagesTabContent
                      pages={archivedPages}
                      selectedPageId={
                        selectedItem?.type === "archivedPage"
                          ? selectedItem.data._id
                          : null
                      }
                      onSelectPage={(page) =>
                        setSelectedItem({ type: "archivedPage", data: page })
                      }
                      onUnarchivePage={handleUnarchivePage}
                      onDeletePage={(pageId, title) =>
                        handleDeleteClick("page", pageId, title)
                      }
                      isUnarchiving={unarchivePageMutation.isPending}
                      isDeleting={deletePageMutation.isPending}
                    />
                  )}
                </div>
              </div>

              {/* Right Content Area - Preview */}
              <div className="lg:col-span-8 bg-white rounded-lg border p-6 min-h-0 overflow-hidden">
                {renderContentPreview()}
              </div>
            </div>
          )}
        </div>
      </Container>

      {/* Delete Confirmation Modal */}
      <ConfirmationBox
        isOpen={isDeleteConfirmationOpen}
        title={`Delete ${itemToDelete?.type === "module" ? "Module" : itemToDelete?.type === "lesson" ? "Lesson" : "Page"}`}
        message={
          <div>
            <p>Are you sure you want to delete this {itemToDelete?.type}?</p>
            <p className="text-salmon-400 font-semibold mt-2">
              &ldquo;{itemToDelete?.title}&rdquo;
            </p>
            {itemToDelete?.type === "module" && (
              <p className="text-blue-600 mt-2 text-sm">
                Note: Lessons in this module will be unlinked but not deleted.
                They will remain in the database and can be used by other
                modules. Bookmarks associated with this module will be deleted.
              </p>
            )}
            {itemToDelete?.type === "lesson" && (
              <>
                <p className="text-blue-600 mt-2 text-sm">
                  Note: Pages associated with this lesson will be unlinked but
                  not deleted. They will remain in the database and can be used
                  by other lessons or deleted separately in the Pages section.
                </p>
                <p className="text-gray-500 mt-2 text-sm">
                  This action cannot be undone.
                </p>
              </>
            )}
            {itemToDelete?.type === "page" && (
              <p className="text-amber-600 mt-2 text-sm">
                The page will be moved to &quot;Pending Deletion&quot; and can
                be restored within 7 days. After that, it will be permanently
                deleted.
              </p>
            )}
            {(itemToDelete?.type === "module" ||
              itemToDelete?.type === "lesson") && (
              <p className="text-gray-500 mt-2 text-sm">
                This action cannot be undone.
              </p>
            )}
          </div>
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setIsDeleteConfirmationOpen(false);
          setItemToDelete(null);
        }}
        onCloseBox={() => {
          setIsDeleteConfirmationOpen(false);
          setItemToDelete(null);
        }}
        danger
      />
    </div>
  );
};

export default ContentDeletePage;
